import { useCallback } from 'react';
import { useUserPref } from './useUserPref';

/**
 * useColumnPrefs — loads per-user column layout for a table and exposes
 * a save function.
 *
 * Storage: user_column_prefs keyed by (owner_id, table_key) via useUserPref; `columns`
 * is a JSONB array of {key, visible} entries (order matters).
 *
 * Resolution (saved DB entries against caller-supplied hardcodedColumns):
 *   - saved key found in hardcoded: keep saved order + visible; the render
 *     fn and label always come from hardcoded (never stored in DB).
 *   - saved key NOT in hardcoded: dropped (stale column, e.g. removed in a
 *     later release).
 *   - hardcoded key NOT in saved: appended at the end, visible=true (new
 *     columns are visible by default).
 *
 * `saved` is the raw stored layout (null until loaded, [] when there is none) for a caller that resolves it
 * itself — the reports store only the columns a user has switched, against per-report defaults.
 *
 * Fallbacks:
 *   - ownerId null/undefined (staff not loaded yet): returns hardcodedColumns
 *     as-is, save() is a no-op.
 *   - No saved row in DB: returns hardcodedColumns as-is.
 *
 * Supabase v2: await + destructure { data, error }. Never `.catch()` directly
 * on the query builder (it's a thenable, not a Promise).
 */
export function useColumnPrefs(tableKey, hardcodedColumns, ownerId) {
  const { saved: savedLayout, save: saveRaw, loading, error } = useUserPref(tableKey, ownerId);

  let columns;
  if (!savedLayout || savedLayout.length === 0) {
    columns = hardcodedColumns;
  } else {
    const hardcodedByKey = new Map(hardcodedColumns.map(c => [c.key, c]));
    const seen = new Set();
    const resolved = [];
    for (const entry of savedLayout) {
      const hc = hardcodedByKey.get(entry?.key);
      if (hc) {
        resolved.push({ ...hc, visible: entry.visible !== false });
        seen.add(entry.key);
      }
    }
    for (const hc of hardcodedColumns) {
      if (!seen.has(hc.key)) resolved.push({ ...hc, visible: true });
    }
    columns = resolved;
  }

  const save = useCallback(
    (newOrderedColumns) => saveRaw(newOrderedColumns.map(col => ({ key: col.key, visible: col.visible !== false }))),
    [saveRaw]);

  return { columns, saved: savedLayout, save, loading, error };
}
